import { isToday } from 'date-fns';
import i18n from '../i18n';
import { taskDisplayName } from './productionEvents';
import { getBenchmarkKey } from './benchmark';
import { calcLabourCost } from './labour';
import { formatDuration, formatCurrency } from './labour';
import { formatTime } from './time';

const BENCHMARK_EXCEED_RATIO = 1.5;
/** Fallback when no benchmark exists (labeled in UI). */
export const LIVE_FALLBACK_REVIEW_MINUTES = 120;
/** No timeline/note activity for this long on a running task. */
export const LIVE_STALE_ACTIVITY_MINUTES = 45;
/** Very long run; likely forgotten timer. */
export const LIVE_STUCK_MINUTES = 8 * 60;

function toDate(raw) {
  if (!raw) return null;
  const d = raw instanceof Date ? raw : raw.toDate?.() ?? new Date(raw);
  return isNaN(d.getTime()) ? null : d;
}

export function getTaskStartTime(task) {
  return toDate(task?.startTime ?? task?.createdAt);
}

/** Net elapsed minutes for an active task (respects recorded pause time). */
export function calcActiveElapsedMinutes(task, asOf = new Date()) {
  const start = getTaskStartTime(task);
  if (!start) return null;

  const pausedMs = (task.pausedMinutes ?? 0) * 60000;
  const asOfMs = asOf instanceof Date ? asOf.getTime() : new Date(asOf).getTime();

  if (task.pausedAt) {
    const pausedAt = toDate(task.pausedAt);
    if (pausedAt) {
      return Math.max(0, Math.round((pausedAt.getTime() - start.getTime() - pausedMs) / 60000));
    }
  }

  return Math.max(0, Math.round((asOfMs - start.getTime() - pausedMs) / 60000));
}

export function estimateActiveLabourCost(task, elapsedMinutes, userRates, defaultTeamRate) {
  if (elapsedMinutes == null || elapsedMinutes <= 0) {
    return { cost: null, source: 'unavailable' };
  }
  const { cost, source } = calcLabourCost(
    elapsedMinutes,
    task.employeeIds,
    task.teamSize,
    userRates,
    defaultTeamRate,
  );
  return { cost, source };
}

function lastActivityForTask(taskId, events) {
  let latest = null;
  for (const ev of events) {
    if (ev.taskId !== taskId || !ev.timestamp) continue;
    if (!latest || ev.timestamp > latest) latest = ev.timestamp;
  }
  return latest;
}

/**
 * Review flags for live tasks. Uses careful language; only flags when data supports it.
 */
export function detectLiveTaskReview(task, {
  elapsedMinutes,
  benchmark,
  lastActivityAt,
  asOf = new Date(),
}) {
  const flags = [];
  const badges = [];
  const isPaused = Boolean(task.pausedAt);

  if (isPaused) {
    badges.push({ key: 'paused', label: i18n.t('replayExtra.liveBadges.paused'), tone: 'neutral' });
  } else {
    badges.push({ key: 'still_running', label: i18n.t('replayExtra.liveBadges.stillRunning'), tone: 'active' });
  }

  const benchDur = benchmark?.netDurationMinutes ?? benchmark?.durationMinutes ?? null;

  if (benchDur && elapsedMinutes != null && elapsedMinutes > benchDur * BENCHMARK_EXCEED_RATIO) {
    flags.push('running_longer');
    badges.push({
      key: 'running_longer',
      label: i18n.t('replayExtra.liveBadges.runningLonger'),
      tone: 'warning',
      detail: i18n.t('replayExtra.liveBadges.runningLongerDetail', {
        usual: formatDuration(benchDur),
        elapsed: formatDuration(elapsedMinutes),
      }),
      usesBenchmark: true,
    });
  } else if (!benchDur && elapsedMinutes != null && elapsedMinutes >= LIVE_FALLBACK_REVIEW_MINUTES) {
    flags.push('may_need_review');
    badges.push({
      key: 'may_need_review',
      label: i18n.t('replayExtra.liveBadges.mayNeedReview'),
      tone: 'warning',
      detail: i18n.t('replayExtra.liveBadges.mayNeedReviewDetail', {
        elapsed: formatDuration(elapsedMinutes),
      }),
      usesBenchmark: false,
    });
  }

  if (elapsedMinutes != null && elapsedMinutes >= LIVE_STUCK_MINUTES) {
    flags.push('may_need_review');
    if (!badges.some((b) => b.key === 'may_need_review')) {
      badges.push({
        key: 'long_elapsed',
        label: i18n.t('replayExtra.liveBadges.mayNeedReview'),
        tone: 'warning',
        detail: i18n.t('replayExtra.liveBadges.longElapsedDetail', {
          elapsed: formatDuration(elapsedMinutes),
        }),
        usesBenchmark: false,
      });
    }
  }

  if (lastActivityAt && !isPaused) {
    const inactiveMin = Math.round((asOf.getTime() - lastActivityAt.getTime()) / 60000);
    if (inactiveMin >= LIVE_STALE_ACTIVITY_MINUTES) {
      flags.push('no_recent_activity');
      badges.push({
        key: 'no_recent_activity',
        label: i18n.t('replayExtra.liveBadges.noRecentActivity'),
        tone: 'warning',
        detail: i18n.t('replayExtra.liveBadges.noActivityDetail', { minutes: inactiveMin }),
      });
    }
  }

  const needsAttention = flags.some(
    (f) => f === 'running_longer' || f === 'may_need_review' || f === 'no_recent_activity',
  );

  return { flags, badges, needsAttention };
}

/**
 * Build enriched live task rows for the replay sidebar and timeline.
 */
export function buildLiveTaskViews({
  activeTasks,
  events,
  benchmarks,
  userRates = {},
  defaultTeamRate = null,
  currency = 'CAD',
  asOf = new Date(),
}) {
  return activeTasks
    .filter((t) => t.status === 'active')
    .map((task) => {
      const start = getTaskStartTime(task);
      const elapsedMinutes = calcActiveElapsedMinutes(task, asOf);
      const bench = benchmarks?.get?.(getBenchmarkKey(task)) ?? null;
      const lastActivityAt = lastActivityForTask(task.id, events);
      const review = detectLiveTaskReview(task, {
        elapsedMinutes,
        benchmark: bench,
        lastActivityAt,
        asOf,
      });
      const { cost, source: costSource } = estimateActiveLabourCost(
        task,
        elapsedMinutes,
        userRates,
        defaultTeamRate,
      );

      const worker = task.employeeNames?.length
        ? task.employeeNames.join(', ')
        : task.teamSize > 1 ? i18n.t('replayExtra.teamX', { size: task.teamSize }) : null;

      return {
        id: task.id,
        task,
        name: taskDisplayName(task),
        worker,
        room: task.roomName || null,
        category: task.taskCategoryName || null,
        product: task.productName || null,
        startTime: start,
        startTimeLabel: start ? formatTime(start) : null,
        elapsedMinutes,
        paused: Boolean(task.pausedAt),
        statusLabel: task.pausedAt
          ? i18n.t('replayExtra.liveBadges.paused')
          : i18n.t('replayExtra.liveBadges.stillRunning'),
        unitsCompleted: task.quantityCompleted ?? null,
        estimatedCost: cost,
        costSource,
        currency,
        benchmarkMinutes: bench?.netDurationMinutes ?? bench?.durationMinutes ?? null,
        lastActivityAt,
        ...review,
      };
    })
    .sort((a, b) => (b.startTime?.getTime?.() ?? 0) - (a.startTime?.getTime?.() ?? 0));
}

export function isReplayLiveMode(selectedDate, activeTaskCount) {
  const d = selectedDate instanceof Date ? selectedDate : new Date(selectedDate);
  return isToday(d) && activeTaskCount > 0;
}

/** Index after which to render the "Now" marker (-1 = before first event). */
export function findNowMarkerIndex(events, now = new Date()) {
  if (!events.length) return { insertAfter: -1, show: true };
  let insertAfter = -1;
  for (let i = 0; i < events.length; i += 1) {
    if (events[i].timestamp <= now) insertAfter = i;
    else break;
  }
  return { insertAfter, show: true };
}

export function enrichEventsWithLiveState(events, liveTasksById, tasksById) {
  return events.map((ev) => {
    const task = tasksById.get(ev.taskId);
    const live = liveTasksById.get(ev.taskId);
    if (task?.status !== 'active' || !live) return ev;

    const next = { ...ev, liveReview: live };
    if (live.flags.includes('running_longer')) {
      next.highlight = 'long-running';
    }
    return next;
  });
}
